// Display mirror of backend/src/lib/exerciseScenarios.ts: the scenario library the Exercises page
// offers under "New from template". The backend list is the source; backend
// lib/__tests__/exerciseScenarios.test.ts fails if the ids or kinds of the two differ.
// Contract: docs/OPERATIONAL-RESILIENCE.md "Incident log, post-incident review and exercise findings".

export interface ScenarioInject {
  /** Minutes after the exercise starts. */
  atMinutes: number;
  prompt: string;
}

export interface ExerciseScenario {
  id: string;
  kind: string;
  title: string;
  scenario: string;
  objectives: string[];
  participantRoles: string[];
  injects: ScenarioInject[];
  successCriteria: string[];
  /** Roughly how long the session runs, in minutes. */
  durationMinutes: number;
}

export const EXERCISE_SCENARIOS: ExerciseScenario[] = [
  {
    id: "ransomware-tabletop",
    kind: "tabletop",
    title: "Ransomware tabletop",
    scenario: "Early on a Monday the service desk receives reports that files on the shared drive cannot be opened and carry a new extension. A ransom note appears on several servers demanding payment within 72 hours and claiming that data has been copied out. Backups are online and their state is unknown.",
    objectives: [
      "Invoke the incident response plan and name an incident lead within the first 30 minutes",
      "Decide on containment (isolation, account resets) with the business impact understood",
      "Work out who must be notified, and by when, from the moment of awareness",
      "Reach a documented decision on the ransom demand, with sanctions screening and law enforcement contact",
      "Confirm that clean, restorable backups exist and how long recovery would take",
    ],
    participantRoles: ["Incident lead", "IT / infrastructure", "Security", "Senior management", "Communications", "Legal or data protection", "Insurer contact (observer)"],
    injects: [
      { atMinutes: 0, prompt: "The service desk has twelve calls about unreadable files. What do you do first, and who do you call?" },
      { atMinutes: 20, prompt: "The ransom note names your organisation and lists three folders it claims to have copied. Does this change who needs to know?" },
      { atMinutes: 40, prompt: "The backup server is also encrypted. The offline copy is four days old. What is the recovery plan and what is lost?" },
      { atMinutes: 60, prompt: "A journalist emails asking for comment on a leak site post. Who answers, and what do they say?" },
      { atMinutes: 80, prompt: "The board asks whether to pay. What information do you need, and who takes the decision?" },
    ],
    successCriteria: [
      "An incident lead and a decision log were in place within 30 minutes",
      "Every notification with a statutory clock was identified with its deadline",
      "The payment decision followed the documented process, including sanctions screening",
      "The recovery time from backups was stated and compared with the recovery objective",
    ],
    durationMinutes: 120,
  },
  {
    id: "key-supplier-outage",
    kind: "supply_chain",
    title: "Key supplier outage",
    scenario: "A supplier that delivers a service you depend on every day (for example payroll, hosting, a line-of-business application or managed IT) announces an unplanned outage with no restoration time. Later that day it confirms a cyber incident on its side.",
    objectives: [
      "Identify which business services depend on the supplier and what stops first",
      "Invoke the workaround or contingency for each affected service",
      "Test the contractual route: incident notification from the supplier, service credits, exit options",
      "Decide whether the supplier's incident is also yours (data held, access into your estate)",
    ],
    participantRoles: ["Incident lead", "Supplier relationship owner", "Business service owners", "IT / security", "Procurement or legal", "The supplier's contact (where they take part)"],
    injects: [
      { atMinutes: 0, prompt: "The supplier's status page says 'investigating'. Which of your services are affected, and how do you know?" },
      { atMinutes: 20, prompt: "Four hours in, there is no update. What does the contract say about how fast they must tell you?" },
      { atMinutes: 40, prompt: "The supplier confirms a cyber incident and that customer data may be involved. Is this now a notifiable incident for you?" },
      { atMinutes: 60, prompt: "The supplier estimates five days to restore. What is the workaround, and can it run that long?" },
    ],
    successCriteria: [
      "The dependent business services were identified from the asset register, not from memory",
      "A working contingency existed for every critical service, or the gap was recorded",
      "The supplier's notification obligation and your own were both established",
    ],
    durationMinutes: 90,
  },
  {
    id: "cloud-region-failover",
    kind: "dr_failover",
    title: "Cloud region or data centre failover",
    scenario: "The primary cloud region (or data centre) hosting your production services becomes unavailable. The provider expects a prolonged outage. The exercise performs, or walks through step by step, the failover to the secondary location.",
    objectives: [
      "Fail over the in-scope services to the secondary location",
      "Measure the time to restore service against the recovery time objective",
      "Measure data loss against the recovery point objective",
      "Confirm monitoring, access control and logging work in the secondary location",
      "Fail back, or document how fail-back would be done",
    ],
    participantRoles: ["Recovery lead", "Cloud / infrastructure engineers", "Application owners", "Security", "Business service owner (to accept the service)"],
    injects: [
      { atMinutes: 0, prompt: "The primary region is unreachable. Who declares a disaster, and on what criteria?" },
      { atMinutes: 30, prompt: "DNS has been switched but one application still points at the primary database. What now?" },
      { atMinutes: 60, prompt: "The secondary location is up. How do you confirm that users can work and that security monitoring is live?" },
    ],
    successCriteria: [
      "Measured recovery time was within the recovery time objective",
      "Measured data loss was within the recovery point objective",
      "Security monitoring and logging were confirmed working after failover",
      "Every manual step that was not in the runbook was recorded as a finding",
    ],
    durationMinutes: 240,
  },
  {
    id: "backup-restore",
    kind: "backup_restore",
    title: "Backup restoration test",
    scenario: "A system or data set that matters to the business is restored from backup into an isolated environment, as it would be after deletion, corruption or ransomware, and checked for completeness.",
    objectives: [
      "Restore a named system or data set from the most recent backup",
      "Restore from the offline or immutable copy, not only the online one",
      "Check the restored data is complete and usable by its owner",
      "Record how long the restore took",
    ],
    participantRoles: ["Backup administrator", "System or data owner", "Security (to confirm the copy is clean)"],
    injects: [
      { atMinutes: 0, prompt: "Which backup copy will you restore from, and how old is it?" },
      { atMinutes: 30, prompt: "The restore needs a credential that is stored on the system being restored. Where else is it held?" },
      { atMinutes: 60, prompt: "The owner reports a missing folder in the restored data. Why, and is it in any other copy?" },
    ],
    successCriteria: [
      "The restore completed and the owner confirmed the data was usable",
      "The offline or immutable copy was restorable",
      "The restore time was within the recovery time objective",
    ],
    durationMinutes: 120,
  },
  {
    id: "business-email-compromise",
    kind: "tabletop",
    title: "Business email compromise and payment fraud",
    scenario: "A supplier emails the finance team from its usual address asking for its bank details to be changed before this month's payment run. The next day the real supplier chases an unpaid invoice. A senior manager's mailbox is found to have an inbox rule forwarding mail to an external address.",
    objectives: [
      "Recognise the compromise and contain the affected mailbox and sessions",
      "Test the payment-change verification procedure",
      "Attempt recovery of the funds through the bank quickly",
      "Establish what the attacker could read, and whether personal data was exposed",
    ],
    participantRoles: ["Incident lead", "Finance", "IT / Microsoft 365 or Google Workspace administrator", "Senior management", "Data protection lead"],
    injects: [
      { atMinutes: 0, prompt: "Finance received the bank change request. What does the procedure say must happen before it is actioned?" },
      { atMinutes: 20, prompt: "The payment was made to the new account yesterday. Who calls the bank, and how quickly?" },
      { atMinutes: 40, prompt: "The forwarding rule has been in place for three weeks. What was forwarded, and does that include personal data?" },
      { atMinutes: 60, prompt: "Other suppliers may have received emails from the compromised mailbox. Who tells them, and what do you say?" },
    ],
    successCriteria: [
      "The payment-change verification step was known and would have stopped the payment",
      "Mailbox containment (session revocation, password reset, rule removal, sign-in review) was complete",
      "The personal data breach assessment was made and recorded within the statutory clock",
    ],
    durationMinutes: 90,
  },
  {
    id: "data-breach-notification",
    kind: "tabletop",
    title: "Personal data breach and regulator notification",
    scenario: "A member of staff reports that a spreadsheet of customer records was shared by a public link and has been accessed from unknown addresses. The scale and the categories of data are unclear at first.",
    objectives: [
      "Assess the breach: what data, how many people, likely consequences",
      "Decide whether the supervisory authority must be notified and meet the clock from awareness",
      "Decide whether the people affected must be told, and draft what they would be told",
      "Record the breach and the reasoning in the breach register, whatever the decision",
    ],
    participantRoles: ["Data protection lead", "Incident lead", "Owner of the data", "IT / security", "Communications", "Legal"],
    injects: [
      { atMinutes: 0, prompt: "When did the organisation become aware, and so when does the notification clock run out?" },
      { atMinutes: 20, prompt: "The spreadsheet includes dates of birth and health notes for some customers. Does that change the assessment?" },
      { atMinutes: 40, prompt: "You do not yet know how many records were viewed. Do you notify now with partial information, or wait?" },
      { atMinutes: 60, prompt: "A customer posts on social media that they received a suspicious email quoting their details. What now?" },
    ],
    successCriteria: [
      "The notification decision was reached and documented inside the statutory window",
      "The breach register entry held the facts, effects and remedial action",
      "A draft notice to the people affected was prepared, or the reason none was needed was recorded",
    ],
    durationMinutes: 90,
  },
  {
    id: "loss-of-premises",
    kind: "contingency_plan",
    title: "Loss of premises",
    scenario: "The main office cannot be used for at least two weeks (for example after a fire, flood or loss of power) and the equipment inside is not reachable. Staff must work from elsewhere from the next working day.",
    objectives: [
      "Account for everyone and communicate with all staff",
      "Get people working from home or an alternate site from the next working day",
      "Recover or replace the equipment, paper records and on-premises systems that matter",
      "Keep information secure while working away from the office",
    ],
    participantRoles: ["Business continuity lead", "Facilities", "IT", "HR", "Department heads", "Communications"],
    injects: [
      { atMinutes: 0, prompt: "It is 19:00 and the building has been evacuated. How do you reach every member of staff tonight?" },
      { atMinutes: 20, prompt: "The on-premises server and the network cabinet are in the building. What stops working tomorrow?" },
      { atMinutes: 40, prompt: "Twenty staff have no laptop. Where do devices come from, and how are they set up securely?" },
      { atMinutes: 60, prompt: "Paper files with personal data are in the building. Who secures them, and how?" },
    ],
    successCriteria: [
      "Every member of staff could be reached using the contact list held outside the building",
      "Critical activities could resume within their recovery time objective",
      "Security controls (device management, access, secure disposal) held for replacement equipment",
    ],
    durationMinutes: 90,
  },
  {
    id: "insider-misuse",
    kind: "incident_walkthrough",
    title: "Insider misuse",
    scenario: "Monitoring shows that an employee who has resigned and is working their notice has downloaded a large volume of customer and pricing data to a personal cloud storage account over the past week.",
    objectives: [
      "Preserve evidence in a way that would stand up to a disciplinary or legal process",
      "Contain access without tipping off prematurely, with HR and legal involved",
      "Decide whether this is a personal data breach and whether it must be notified",
      "Test the leaver process: when access is removed and how that is checked",
    ],
    participantRoles: ["Incident lead", "HR", "Legal", "IT / security", "The employee's manager", "Data protection lead"],
    injects: [
      { atMinutes: 0, prompt: "What evidence do you capture before anything else changes, and who is allowed to look at it?" },
      { atMinutes: 20, prompt: "HR wants to call the employee in today. Security wants another day of monitoring. Who decides?" },
      { atMinutes: 40, prompt: "The employee says the files were for handover. How do you test that?" },
      { atMinutes: 60, prompt: "The data includes customers' personal data. Is this a notifiable breach?" },
    ],
    successCriteria: [
      "Evidence was preserved with a record of who handled it",
      "Access was removed in a coordinated way with HR and legal",
      "The breach assessment was made and recorded",
      "A gap in the leaver process, if any, was recorded as a finding",
    ],
    durationMinutes: 90,
  },
];

export function scenarioById(id: string | null | undefined): ExerciseScenario | null {
  if (!id) return null;
  return EXERCISE_SCENARIOS.find((s) => s.id === id) ?? null;
}

/** The form fields a template fills: objectives and success criteria as text, the injects as a run sheet. */
export function scenarioFormText(s: ExerciseScenario): { scenario: string; objectives: string; participants: string } {
  const injects = s.injects.map((i) => `+${i.atMinutes} min: ${i.prompt}`).join("\n");
  return {
    scenario: `${s.scenario}\n\nInjects:\n${injects}`,
    objectives: `${s.objectives.map((o) => `- ${o}`).join("\n")}\n\nSuccess criteria:\n${s.successCriteria.map((c) => `- ${c}`).join("\n")}`,
    participants: s.participantRoles.join(", "),
  };
}
